import { createContext, useContext, useRef } from "react";
import PropTypes from "prop-types";
import ReactTags from "react-tag-autocomplete";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	MouseSensor,
	TouchSensor,
	useSensor,
	useSensors
} from "@dnd-kit/core";
import {
	SortableContext,
	rectSortingStrategy,
	sortableKeyboardCoordinates,
	useSortable
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// True while a drag is in progress (and right after it): the click that ends
// a drag must not remove the tag
const DraggingContext = createContext({ current: false });

// Same markup as the default react-tag-autocomplete tag, made sortable
function SortableTag({ tag, removeButtonText, classNames, onDelete }) {
	const dragging = useContext(DraggingContext);
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging
	} = useSortable({ id: tag._id });

	return (
		<button
			type="button"
			ref={setNodeRef}
			{...attributes}
			{...listeners}
			className={classNames.selectedTag}
			title={`${removeButtonText} (glisser pour réordonner)`}
			style={{
				transform: CSS.Transform.toString(transform),
				transition,
				cursor: isDragging ? "grabbing" : "grab",
				opacity: isDragging ? 0.7 : 1,
				zIndex: isDragging ? 1 : undefined,
				position: "relative"
			}}
			onClick={event => {
				if (!dragging.current) {
					onDelete(event);
				}
			}}
		>
			<span className={classNames.selectedTagName}>{tag.name}</span>
		</button>
	);
}

/**
 * react-tag-autocomplete whose selected tags can be reordered by drag & drop
 * (mouse, touch with a long press, keyboard). Tags must have an `_id`.
 * onReorder(fromIndex, toIndex) is called when a tag is dropped elsewhere.
 */
export default function SortableTags({ tags, onReorder, ...reactTagsProps }) {
	const dragging = useRef(false);
	const sensors = useSensors(
		// A few pixels of movement before dragging, so that a click still removes the tag
		useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
		// Long press on touch screens, so that the page can still be scrolled
		useSensor(TouchSensor, {
			activationConstraint: { delay: 250, tolerance: 5 }
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates
		})
	);

	const endDrag = () => {
		// Released after the click event that follows the drop
		setTimeout(() => {
			dragging.current = false;
		});
	};

	const handleDragEnd = ({ active, over }) => {
		endDrag();
		if (over && active.id !== over.id) {
			const from = tags.findIndex(tag => tag._id === active.id);
			const to = tags.findIndex(tag => tag._id === over.id);
			if (from >= 0 && to >= 0) {
				onReorder(from, to);
			}
		}
	};

	return (
		<DraggingContext.Provider value={dragging}>
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragStart={() => {
					dragging.current = true;
				}}
				onDragEnd={handleDragEnd}
				onDragCancel={endDrag}
			>
				<SortableContext
					items={tags.map(tag => tag._id)}
					strategy={rectSortingStrategy}
				>
					<ReactTags
						{...reactTagsProps}
						tags={tags}
						tagComponent={SortableTag}
					/>
				</SortableContext>
			</DndContext>
		</DraggingContext.Provider>
	);
}

SortableTags.propTypes = {
	tags: PropTypes.arrayOf(
		PropTypes.shape({
			_id: PropTypes.string.isRequired,
			name: PropTypes.string
		})
	).isRequired,
	onReorder: PropTypes.func.isRequired
};
